import { mergeProps } from '@creator/ui/utils/merge-props/merge-props';
import { useOnClickOutside } from '@src/hooks/use-on-click-outside';
import { propsEqualityFn } from '@src/utils/object-utils/object-utils';
import { noop } from '@src/utils/utils';
import React, { type FC, memo, type ReactNode, useRef } from 'react';
import Icon, { type IconType } from '../Icon/Icon';
import Popover, { type PopoverProps } from '../Popover/Popover';

export interface MenuPopoverProps {
    className?: string;
    triggerIconName?: IconType;
    popoverProps: Omit<PopoverProps, 'trigger'>;
    renderTrigger?: () => ReactNode;
    onClickOutside?: () => void;
    onTriggerClick?: () => void;
    children?: ReactNode;
}

const MenuPopover: FC<MenuPopoverProps> = props => {
    const { children, className, onClickOutside = noop } = props;

    const ref = useRef(undefined);
    useOnClickOutside(ref, onClickOutside);

    function getTriggerClassName(): string {
        const base = 'transition h-6 w-6 flex justify-center items-center rounded-sm cursor-pointer bg-gray-100 dark:bg-gray-800 text-gray-500 text-md';
        return `${base} `;
    }

    function renderPopover() {
        const { popoverProps } = props;
        const _props = mergeProps<[PopoverProps, Partial<PopoverProps>]>(
            {
                trigger: renderTrigger(),
            },
            popoverProps
        );

        return <Popover {..._props}>{children}</Popover>;
    }

    function renderTrigger() {
        const { onTriggerClick = noop, renderTrigger, triggerIconName = 'dotsVertical' } = props;
        if (renderTrigger) return renderTrigger();

        return (
            <div className={getTriggerClassName()} onClick={onTriggerClick}>
                <Icon name={triggerIconName} />
            </div>
        );
    }

    return (
        <div ref={ref} className={`${className}`}>
            {renderPopover()}
        </div>
    );
};

export default memo(MenuPopover, propsEqualityFn);
